import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BookingScheduleInterface } from "../../features/Booking-Form/typings/booking.types";
import { ILocationInventory } from "../FacilityManager/Domain/Types/FloorPlan/LocationInventory.type";
import { IFloorInventory } from "../FacilityManager/Domain/Types/FloorPlan/FloorInventory.type";
import { IPlaceInventory } from "../FacilityManager/Domain/Types/FloorPlan/PlaceInventory.type";
import { IZoneInventory } from "../FacilityManager/Domain/Types/FloorPlan/ZoneInventory.type";

type P = {
  schedule: BookingScheduleInterface;
};

/** Matches API / schedule payload when the booking is mobile working (see `t("Mobile Working")` in UI). */
export function isScheduleMobileWorkingLocation(schedule: { locationName?: string }): boolean {
  return schedule.locationName === "Mobile Working";
}

/** Joins street lines; omits null/undefined/empty parts so "null" never appears in the UI. */
function formatStreetAddress(
  address1: string | null | undefined,
  address2: string | null | undefined
): string {
  return [address1, address2].filter(part => part != null && part !== "").join(" ");
}

export const ScheduleEventLocation: React.FC<P> = ({ schedule }) => {
  const { t } = useTranslation();
  const { locationName, location, floorName, bookingInventory } = schedule;

  const inv = bookingInventory as IPlaceInventory | IZoneInventory;
  const locationInventory = inv?.locationInventory;
  const floorInventory = inv?.floorInventory;

  const { address, postalCode, city } = generateAddressDetail(locationInventory, floorInventory);

  const isMobileWorking = isScheduleMobileWorkingLocation(schedule);

  return (
    <Box>
      <Typography data-testid="schedule-item-location">
        {isMobileWorking ? t("Mobile Working") : locationName ?? location}
        {!isMobileWorking && floorName && ` - ${floorName}`}
      </Typography>

      {!isMobileWorking && (locationInventory || floorInventory) && (
        <>
          <Typography sx={{ mt: 0.2 }}>{address}</Typography>
          <Typography sx={{ mt: 0.2 }}>
            {postalCode}, {city}
          </Typography>
        </>
      )}
    </Box>
  );
};

export function generateAddressDetail(
  locInventory: ILocationInventory | undefined,
  floInventory: IFloorInventory | undefined
) {
  let address: string = "";
  let postalCode: string = "";
  let city: string = "";

  if (locInventory) {
    address = formatStreetAddress(locInventory.address1, locInventory.address2);
    postalCode = locInventory.postalCode;
    city = locInventory.city;
    return { address, postalCode, city };
  }

  if (floInventory) {
    address = formatStreetAddress(
      floInventory.locationInventory.address1,
      floInventory.locationInventory.address2
    );
    postalCode = floInventory.locationInventory.postalCode;
    city = floInventory.locationInventory.city;
    return { address, postalCode, city };
  }
  return { address, postalCode, city };
}
